import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {StationModel} from '@/types/channel/station/station.model';
import {StationStore} from './types';

/**
 * 방송국 상태를 관리하는 zustand 스토어
 * StationModel을 기준으로 도메인별로 명확하게 분리된 구조
 */
const useStationStore = create<StationStore>()(
  devtools(
    (set, get) => ({
      // 🏠 방송국 데이터 도메인
      data: {
        station: {},
        timestamp: '',
        links: [],
      },

      // 👤 사용자 권한 도메인
      user: {
        // 스트리머 <-> 로그인 한 유저의 관계 정보
        userPermissions: {},
        // 로그인 한 유저의 정보
        authentication: {},
      },

      // 💼 비즈니스 로직 도메인
      business: {
        subscription: {},
        adminConfig: {},
      },

      // 🎨 UI 상태 도메인
      ui: {
        loading: true,
        error: null,
        timestamp: '',
      },

      // 도메인별 액션들
      dataActions: {
        updateStation: (station) => {
          set((state) => ({
            data: {
              ...state.data,
              station: {
                ...state.data.station,
                ...station,
              },
            },
          }));
        },

        updateStationDetail: (detail) => {
          set((state) => ({
            data: {
              ...state.data,
              station: {
                ...state.data.station,
                detail: {
                  ...(state.data.station?.detail || {}),
                  ...detail,
                },
              } as Partial<StationModel>,
            },
          }));
        },

        updateStationDisplay: (display) => {
          set((state) => ({
            data: {
              ...state.data,
              station: {
                ...state.data.station,
                display: {
                  ...(state.data.station?.display || {}),
                  ...display,
                },
              } as Partial<StationModel>,
            },
          }));
        },

        updateChannelArt: (channelArt) => {
          set((state) => ({
            data: {
              ...state.data,
              station: {
                ...state.data.station,
                channelArt: {
                  ...(state.data.station?.channelArt || {}),
                  ...channelArt,
                },
              } as Partial<StationModel>,
            },
          }));
        },

        updateBadges: (badges) => {
          set((state) => ({
            data: {
              ...state.data,
              station: {
                ...state.data.station,
                badges,
              },
            },
          }));
        },

        updateMedals: (medals) => {
          set((state) => ({
            data: {
              ...state.data,
              station: {
                ...state.data.station,
                medals,
              },
            },
          }));
        },

        updateStats: (stats) => {
          set((state) => ({
            data: {
              ...state.data,
              station: {
                ...state.data.station,
                stats: {
                  ...(state.data.station?.stats || {}),
                  ...stats,
                },
              } as Partial<StationModel>,
            },
          }));
        },

        updateTimestamp: (timestamp) => {
          set((state) => ({
            data: {
              ...state.data,
              timestamp,
            },
          }));
        },

        updateLinks: (links) => {
          set((state) => ({
            data: {
              ...state.data,
              links,
            },
          }));
        },
      },

      userActions: {
        updateUserPermissions: (permissions) => {
          set((state) => ({
            user: {
              ...state.user,
              userPermissions: {
                ...state.user.userPermissions,
                ...permissions,
              },
            },
          }));
        },

        togglePermission: (permission) => {
          set((state) => ({
            user: {
              ...state.user,
              userPermissions: {
                ...state.user.userPermissions,
                [permission]: !state.user.userPermissions?.[permission],
              },
            },
          }));
        },

        updateAuthentication: (authentication) => {
          set((state) => ({
            user: {
              ...state.user,
              authentication: {
                ...state.user.authentication,
                ...authentication,
              },
            },
          }));
        },
      },

      businessActions: {
        updateSubscription: (subscription) => {
          set((state) => ({
            business: {
              ...state.business,
              subscription: {
                ...state.business.subscription,
                ...subscription,
              },
            },
          }));
        },

        updateAdminConfig: (adminConfig) => {
          set((state) => ({
            business: {
              ...state.business,
              adminConfig: {
                ...state.business.adminConfig,
                ...adminConfig,
              },
            },
          }));
        },
      },

      uiActions: {
        setLoading: (loading) => {
          set((state) => ({
            ui: {
              ...state.ui,
              loading,
            },
          }));
        },

        setError: (error) => {
          set((state) => ({
            ui: {
              ...state.ui,
              error,
            },
          }));
        },

        setTimestamp: (timestamp) => {
          set((state) => ({
            ui: {
              ...state.ui,
              timestamp,
            },
          }));
        },
      },

      // 전체 스토어 액션
      reset: () => {
        set({
          data: {
            station: {},
            timestamp: '',
            links: [],
          },
          user: {
            userPermissions: {},
            authentication: {},
          },
          business: {
            subscription: {},
            adminConfig: {},
          },
          ui: {
            loading: false,
            error: null,
            timestamp: '',
          },
        });
      },

      initialize: (initData) => {
        set((state) => ({
          data: {
            ...state.data,
            ...(initData.station && {station: initData.station}),
            ...(initData.timestamp && {timestamp: initData.timestamp}),
          },
          user: {
            ...state.user,
            ...(initData.userPermissions && {
              userPermissions: initData.userPermissions,
            }),
            ...(initData.authentication && {
              authentication: initData.authentication,
            }),
          },
          business: {
            ...state.business,
            ...(initData.subscription && {subscription: initData.subscription}),
            ...(initData.adminConfig && {adminConfig: initData.adminConfig}),
          },
        }));
      },
    }),
    {
      name: 'station-info-store',
    },
  ),
);

export default useStationStore;
